/**
 * A single leaderboard row.
 *
 * Deliberately much lighter than `ProductCard`. A leaderboard is scanned, not
 * studied: the eye should land on rank → photo → name → one number, and nothing
 * else should compete. Everything secondary is one muted line, and the full
 * breakdown lives on the product page a tap away.
 *
 * Common leaderboard patterns this follows: a fixed-width rank gutter so
 * positions align down the column, a square thumbnail for recognition, a single
 * dominant metric right-aligned, and podium treatment for the top three only.
 */

import { Link } from 'react-router-dom';
import type { MetricId } from '@/lib/nutrition/calculations';
import { METRICS } from '@/lib/nutrition/calculations';
import type { AnalysedProduct } from '@/lib/nutrition/ranking';
import { formatCurrency, formatMetricValue, formatPackage, metricUnit } from '@/lib/format';

interface Props {
  /**
   * How this row should read.
   *
   *   shop      — ranked product from the selected shop
   *   affiliate — ranked, earned its position, and IS advertising
   *   promoted  — paid placement with no rank at all
   */
  kind?: 'shop' | 'affiliate' | 'promoted';
  /** True when another row holds the same rank, e.g. joint 2nd. */
  sharesRank?: boolean;
  /** Which shop it comes from, shown when it is not the selected one. */
  shopLabel?: string | null;
  row: AnalysedProduct;
  /** The number this board ranks by — the one figure that gets prominence. */
  metric: MetricId | 'valueScore';
  /** Set when several criteria are combined: shown instead of the metric. */
  combinedScore?: number | null;
  /** e.g. 2 of 3 chosen criteria — a partial match must never look complete. */
  criteriaMet?: number | null;
  criteriaTotal?: number | null;
  isComparing: boolean;
  compareDisabled: boolean;
  onToggleCompare: (id: string, name: string) => void;
}

export function LeaderboardRow({
  row,
  metric,
  combinedScore = null,
  criteriaMet = null,
  criteriaTotal = null,
  isComparing,
  compareDisabled,
  onToggleCompare,
  kind = 'shop',
  sharesRank = false,
  shopLabel,
}: Props): React.JSX.Element {
  const { product, metrics, rank } = row;
  const podium = rank !== null && rank <= 3 ? rank : null;

  const value =
    metric === 'valueScore'
      ? { value: row.score.score, unavailableReason: row.score.unavailableReason }
      : metrics.values[metric];

  const unit = metric === 'valueScore' ? '' : metricUnit(metric, metrics.currency);

  return (
    <li
      className={[
        'lb-row',
        podium !== null ? `lb-podium-${podium}` : '',
        kind === 'affiliate' ? 'is-affiliate' : '',
        sharesRank ? 'shares-rank' : '',
      ].filter(Boolean).join(' ')}
    >
      {/*
        The whole row is the tap target. A single absolutely-positioned link
        covers it: one real <a>, so screen readers announce a link and
        open-in-new-tab still works, with no nested interactive elements.
      */}
      <Link
        className="lb-rowlink"
        to={`/product/${encodeURIComponent(product.id)}`}
        // Carry the product so the detail page renders instantly, and still
        // shows something if the API is briefly over its read limit.
        state={{ product }}
        aria-label={`${product.name} — view details`}
      />

      {/* Rank is a numeral first; the medal colour is decoration on top of it. */}
      <span
        className={`lb-rank${podium !== null ? ` rank-medal-${podium}` : ''}`}
        aria-label={rank === null ? 'Not ranked' : `Rank ${rank}`}
      >
        {rank ?? '—'}
      </span>

      {product.imageUrl === null ? (
        <span className="lb-thumb lb-thumb-empty" aria-hidden="true">
          ▤
        </span>
      ) : (
        <img
          className="lb-thumb"
          src={product.imageUrl}
          alt=""
          loading="lazy"
          decoding="async"
          width={52}
          height={52}
        />
      )}

      <span className="lb-main">
        <span className="lb-name">
          {product.name}
          {kind === 'affiliate' && (
            <>
              {shopLabel && <span className="lb-shop-chip">{shopLabel}</span>}
              <span className="lb-shop-chip lb-ad-tag">Ad</span>
            </>
          )}
        </span>
        <span className="lb-sub">
          {formatCurrency(metrics.values.price.value, metrics.currency)}
          {product.quantity && ` · ${formatPackage(product.quantity)}`}
          {product.brand && ` · ${product.brand}`}
          {product.zoneName && (
            <span className="lb-zone"> · {product.zoneName}</span>
          )}
        </span>
      </span>

      <span className="lb-metric">
        {combinedScore !== null ? (
          <>
            <span className="lb-metric-value">{Math.round(combinedScore)}</span>
            <span className="lb-metric-unit">
              {criteriaMet !== null && criteriaTotal !== null && criteriaMet < criteriaTotal
                ? `${criteriaMet}/${criteriaTotal} criteria`
                : 'match'}
            </span>
          </>
        ) : (
        <>
        <span className={`lb-metric-value${value?.value === null ? ' na' : ''}`}>
          {value?.value === null
            ? 'N/A'
            : metric === 'valueScore'
              ? String(row.score.score)
              : formatMetricValue(metric, value, metrics.currency, metrics.packageKind)}
        </span>
        <span className="lb-metric-unit">
          {metric === 'valueScore' ? 'score' : unit}
        </span>
        </>
        )}
      </span>

      <button
        type="button"
        className={`lb-compare${isComparing ? ' is-on' : ''}`}
        onClick={() => onToggleCompare(product.id, product.name)}
        disabled={!isComparing && compareDisabled}
        aria-pressed={isComparing}
        aria-label={
          isComparing
            ? `Remove ${product.name} from comparison`
            : `Add ${product.name} to comparison`
        }
        title={metric === 'valueScore' ? undefined : METRICS[metric].formula}
      >
        {isComparing ? '✓' : '+'}
      </button>
    </li>
  );
}
